import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * WebMCPManager component handles the registration of site tools 
 * via the browser's Model Context Protocol (WebMCP) API.
 */
const WebMCPManager = () => {
    const navigate = useNavigate();

    useEffect(() => {
        // Check if WebMCP is supported in the browser
        if (!window.navigator.modelContext?.registerTool) {
            console.debug('WebMCP (navigator.modelContext.registerTool) is not supported in this browser.');
            return;
        }

        const controller = new AbortController();

        try {
            // 1. Tool: Navigate to platform sections
            window.navigator.modelContext.registerTool({
                name: 'navigate_to',
                description: 'Navigate the user to a specific section of the Upload to URL platform.',
                inputSchema: {
                    type: 'object',
                    properties: {
                        destination: {
                            type: 'string',
                            enum: ['pricing', 'dashboard', 'integrations', 'api-docs', 'security', 'faq', 'home'],
                            description: 'The section to navigate to.'
                        }
                    },
                    required: ['destination']
                },
                execute: async ({ destination }) => {
                    const paths = {
                        'pricing': '/pricing',
                        'dashboard': '/dashboard',
                        'integrations': '/integrations',
                        'api-docs': '/api-docs',
                        'security': '/security',
                        'faq': '/faq',
                        'home': '/'
                    };
                    const target = paths[destination] || '/';
                    navigate(target);
                    return { 
                        success: true, 
                        message: `Navigated to ${destination}`,
                        path: target
                    };
                }
            }, { signal: controller.signal });

            // 2. Tool: Get Pricing Information
            window.navigator.modelContext.registerTool({
                name: 'get_pricing_summary',
                description: 'Retrieve current pricing plans, upload limits, and features.',
                inputSchema: {
                    type: 'object',
                    properties: {}
                },
                execute: async () => {
                    return {
                        success: true,
                        plans: [
                            { name: 'Free', limit: '15 uploads/mo', maxSize: '5MB', price: '$0' },
                            { name: 'Starter', limit: '1,000 uploads/mo', maxSize: '16MB', price: '$11/mo' },
                            { name: 'Pro', limit: '6,000 uploads/mo', maxSize: '32MB', price: '$39/mo' },
                            { name: 'Business', limit: '15,000 uploads/mo', maxSize: '100MB', price: '$99/mo' }
                        ],
                        features: ['Unlimited Bandwidth', 'Permanent Hosting', 'Instant Preview', 'REST API Access']
                    };
                }
            }, { signal: controller.signal });

            // 3. Tool: Get Supported Formats
            window.navigator.modelContext.registerTool({
                name: 'get_supported_formats',
                description: 'Returns the list of file extensions and formats supported by the platform.',
                inputSchema: {
                    type: 'object',
                    properties: {}
                },
                execute: async () => {
                    return {
                        success: true,
                        extensions: ['.jpg', '.jpeg', '.png', '.gif', '.pdf', '.mp4', '.webm', '.mp3', '.wav', '.m4a', '.mpga', '.opus', '.bin', '.jfif', '.avif', '.psd', '.vcf', '.hex', '.srt', '.vtt', '.ttf', '.woff', '.woff2', '.mpeg', '.rdp', '.heif', '.heic', '.aac', '.swf', '.msg', '.m3u', '.ai'],
                        categories: {
                            image: ['.jpg', '.jpeg', '.png', '.gif', '.jfif', '.avif', '.psd', '.heif', '.heic', '.ai'],
                            document: ['.pdf', '.vcf', '.srt', '.vtt', '.msg'],
                            video: ['.mp4', '.webm', '.mpeg'],
                            audio: ['.mp3', '.wav', '.m4a', '.mpga', '.opus', '.aac', '.m3u'],
                            font: ['.ttf', '.woff', '.woff2'],
                            other: ['.bin', '.hex', '.rdp', '.swf']
                        }
                    };
                }
            }, { signal: controller.signal });

            console.info('WebMCP tools registered successfully.');

        } catch (error) {
            console.error('Failed to register WebMCP tools:', error);
        }

        return () => {
            // Unregister tools when component unmounts
            controller.abort();
        };
    }, [navigate]);

    return null; // This component has no UI
};

export default WebMCPManager;
